/**
 * Which instrument types live under each asset class.
 *
 * The admin previously assumed a binary `BOND : MUTUAL_FUND`, so the EQUITIES
 * page sent `EQUITIES + MUTUAL_FUND` and the gateway answered "The selected
 * instrument or asset class is not supported!" — 207 public equities and 15
 * IPOs were unreachable.
 *
 * EQUITIES holds two concrete types, so it needs a chooser rather than a
 * single mapping.
 */
export const INSTRUMENT_KEYS_BY_ASSET_CLASS = {
  FIXED_INCOMES: [{key: 'BOND', label: 'Bonds', create: 'Bond', route: 'bond'}],
  FUNDS: [
    {
      key: 'MUTUAL_FUND',
      label: 'Mutual funds',
      create: 'Mutual fund',
      route: 'mutual-funds',
    },
  ],
  EQUITIES: [
    {
      key: 'PUBLIC_EQUITY',
      label: 'Public equities',
      create: 'Public equity',
      route: 'public-equity',
    },
    {key: 'IPO', label: 'IPOs', create: 'IPO', route: 'ipo'},
  ],
};

/**
 * The InstrumentKey behind a create/edit route segment ('bond', 'ipo', …).
 *
 * Derived from the map above rather than written out again, so a type added
 * there is reachable here without a second edit.
 */
export const instrumentKeyForRoute = route => {
  const match = Object.values(INSTRUMENT_KEYS_BY_ASSET_CLASS)
    .reduce((all, types) => all.concat(types), [])
    .filter(type => type.route === route)[0];
  return match?.key;
};

/**
 * Human label for an instrument key.
 *
 * The raw enum was leaking into the UI in at least two different treatments —
 * a bare `MUTUAL_FUND` chip in the instrument picker, and an ad-hoc
 * `replace(/_/g, ' ')` producing "MUTUAL FUND" in the orders table. This is
 * the one place that decides.
 *
 * Covers the two keys the asset-class map does not: PRIVATE_EQUITY and
 * VENTURE_FUND are valid communication destinations but have no asset-class
 * page of their own.
 */
export const INSTRUMENT_KEY_LABELS = {
  BOND: 'Bond',
  MUTUAL_FUND: 'Mutual fund',
  PUBLIC_EQUITY: 'Public equity',
  IPO: 'IPO',
  PRIVATE_EQUITY: 'Private equity',
  VENTURE_FUND: 'Venture fund',
  // Legacy value still present on older order rows.
  STOCK: 'Stock',
};

/**
 * The instrument types an order can be placed on, as `{value, label}` pairs
 * for a Select.
 *
 * Order screens each wrote their own list and still offered the pre-v2 names
 * `STOCK` and `VENTURE`. Those are not InstrumentKey members, so pairing one
 * with an instrument id from `getFinancialInstruments` describes something the
 * gateway has no record of.
 */
export const ORDER_INSTRUMENT_KEYS = [
  'PUBLIC_EQUITY',
  'BOND',
  'MUTUAL_FUND',
  'PRIVATE_EQUITY',
  'VENTURE_FUND',
];

/**
 * Tag colour per instrument key, for the Instrument column of an order table.
 *
 * Both order tables wrote the same three-entry map inline and neither knew any
 * v2 key, so a public equity or IPO order rendered as an uncoloured tag.
 * `STOCK` keeps the colour it always had, since legacy rows still carry it.
 */
const INSTRUMENT_KEY_TAG_COLORS = {
  BOND: 'cyan',
  MUTUAL_FUND: 'purple',
  PUBLIC_EQUITY: 'red',
  STOCK: 'red',
  IPO: 'gold',
  PRIVATE_EQUITY: 'magenta',
  VENTURE_FUND: 'geekblue',
};

export const instrumentKeyColor = key =>
  INSTRUMENT_KEY_TAG_COLORS[key] ?? 'default';

/** `{value, label}` options for the given keys, labelled from the map above. */
export const instrumentKeyOptions = (keys = ORDER_INSTRUMENT_KEYS) =>
  keys.map(key => ({value: key, label: INSTRUMENT_KEY_LABELS[key] ?? key}));

export const instrumentKeyLabel = key =>
  INSTRUMENT_KEY_LABELS[key] ??
  // Anything new from the backend still reads as words rather than an enum.
  (key
    ? String(key)
        .toLowerCase()
        .split('_')
        .map((word, index) =>
          index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
        )
        .join(' ')
    : '—');

/** Asset class keys are shown as often as instrument keys. */
export const ASSET_CLASS_LABELS = {
  EQUITIES: 'Equities',
  FIXED_INCOMES: 'Fixed income',
  FUNDS: 'Funds',
};

export const assetClassLabel = key =>
  ASSET_CLASS_LABELS[key] ?? instrumentKeyLabel(key);

export const instrumentKeysFor = assetClassKey =>
  INSTRUMENT_KEYS_BY_ASSET_CLASS[assetClassKey] ?? [
    {
      key: 'MUTUAL_FUND',
      label: 'Instruments',
      create: 'Mutual fund',
      route: 'mutual-funds',
    },
  ];

/** The type a page defaults to when the asset class holds more than one. */
export const defaultInstrumentKey = assetClassKey =>
  instrumentKeysFor(assetClassKey)[0].key;

/**
 * The update/create route segment for a concrete instrument.
 *
 * EQUITIES holds both PUBLIC_EQUITY (`public-equity`) and IPO (`ipo`), so the
 * asset-class key alone cannot pick the route — the instrument key must. When
 * the key is unknown (e.g. a page reached by a bare URL) this falls back to the
 * asset class's first/default type rather than guessing `mutual-funds`.
 */
export const routeForInstrument = (assetClassKey, instrumentKey) => {
  const options = instrumentKeysFor(assetClassKey);
  return (
    options.find(entry => entry.key === instrumentKey)?.route ??
    options[0].route
  );
};

/**
 * Which row actions each instrument type actually supports.
 *
 * The actions menu offered all of them on every row, so an operator could open
 * "Update Maturity Status" on a mutual fund — a mutation named
 * `updateBondMaturityStatus`, whose own modal copy talks about bonds — or
 * "Update Instrument Valuation" on an IPO, which posts a
 * `mutualFundValuationHistoryInput`. Both would have been rejected by the
 * gateway after the operator filled the form.
 *
 *   maturity   `updateBondMaturityStatus` — only bonds have a maturity date.
 *   valuation  NAV history, keyed to `mutualFundValuationHistoryInput`.
 *   inventory  `inventoryPosition` is absent from PublicEquity entirely, and
 *              unreadable on Ipo (see below), so only bonds and funds get it.
 *
 * Unsupported actions are omitted rather than disabled: a greyed-out row reads
 * as "you lack permission", which is a different problem from "this does not
 * apply to this instrument".
 */
const INSTRUMENT_ACTIONS = {
  BOND: {maturity: true, valuation: false, inventory: true},
  MUTUAL_FUND: {maturity: false, valuation: true, inventory: true},
  // IPOs do carry inventory position records — all 15 have one — but every
  // value field on them (availableUnits, averageUnitCost, lowUnitThreshold,
  // autoBuyThresholdAmount, instrumentId) is declared non-nullable and stored
  // null, so selecting any of them nulls the whole object. The group is hidden
  // until the backend populates them; flip this to true once it does.
  IPO: {maturity: false, valuation: false, inventory: false},
  PUBLIC_EQUITY: {maturity: false, valuation: false, inventory: false},
};

export const instrumentSupports = (instrumentKey, action) =>
  INSTRUMENT_ACTIONS[instrumentKey]?.[action] ?? false;

/**
 * Inventory prediction is a managed-fund feature.
 *
 * The whole page is `MutualFundsInventoryPrediction`, its own copy says
 * "managed mutual funds", and every mutation behind it is named
 * `*ManagedFundInventoryConfig` / `runManagedFundInventoryPrediction`. It was
 * shown on every asset class except fixed income, so EQUITIES offered a tab
 * whose forecasts and configuration could never apply to anything on it.
 */
export const supportsInventoryPrediction = assetClassKey =>
  assetClassKey === 'FUNDS';
